// mejora-web F4: piezas comunes de vídeos (portada y biblioteca /videos).
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Play, Lock, X, Loader2, Film, ExternalLink } from 'lucide-react';
import { API_BASE } from '../lib/api';

export const CATEGORIAS = ['suave', 'dinamico', 'respiracion'];

function authHeaders() {
  let tok = null;
  try { tok = localStorage.getItem('idoyoga_auth_token'); } catch (_) { /* sin storage */ }
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

// Lista de vídeos (con sesión, el backend marca bloqueados los de membresía si no la hay).
export function useVideos(lang, { soloPublicos = false } = {}) {
  const [estado, setEstado] = useState({ data: null, membresia: false, error: null });
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/videos?idioma=${lang}${soloPublicos ? '&solo_publicos=1' : ''}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((j) => { if (!cancelled) setEstado({ data: j.data || [], membresia: !!j.membresia, error: null }); })
      .catch((e) => { if (!cancelled) setEstado({ data: null, membresia: false, error: e.message }); });
    return () => { cancelled = true; };
  }, [lang, soloPublicos]);
  return estado;
}

export function FiltroCategorias({ valor, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="videos-filtro" role="group" aria-label={t('videos.filtro')}>
      {['todas', ...CATEGORIAS].map((c) => (
        <button key={c} type="button" className={'filtro-seg-btn' + (valor === c ? ' active' : '')} onClick={() => onChange(c)}>
          {c === 'todas' ? t('videos.categoria.todas') : t(`videos.categoria.${c}`)}
        </button>
      ))}
    </div>
  );
}

export function VideoCard({ video, onPlay, onBloqueado }) {
  const { t } = useTranslation();
  // Vídeos gratuitos: la imagen es el segundo 3 del propio vídeo.
  const [frame, setFrame] = useState(null);
  useEffect(() => {
    if (video.bloqueado || video.visibilidad !== 'publico') return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/videos/${video.id}/reproducir`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j && j.tipo === 'video' && j.url) setFrame(j.url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [video.id, video.bloqueado, video.visibilidad]);
  const click = () => (video.bloqueado ? onBloqueado(video) : onPlay(video));
  return (
    <article className={'video-card' + (video.bloqueado ? ' is-bloqueado' : '')}>
      <button type="button" className="video-card-media" onClick={click} aria-label={video.titulo}>
        {frame
          // eslint-disable-next-line jsx-a11y/media-has-caption
          ? <video src={`${frame}#t=3`} preload="metadata" muted playsInline tabIndex={-1} />
          : video.portada_url
          ? <img src={video.portada_url} alt="" loading="lazy" />
          : <span className="video-card-placeholder"><Film size={30} strokeWidth={1} /></span>}
        <span className="video-card-play">{video.bloqueado ? <Lock size={18} /> : <Play size={18} />}</span>
        {video.duracion_minutos && <span className="video-card-dur">{t('clases.duracion_min', { min: video.duracion_minutos })}</span>}
      </button>
      <div className="video-card-body">
        <h3 className="video-card-titulo">{video.titulo}</h3>
        {video.tipo === 'grabacion_clase' && <span className="video-card-tipo">{t('videos.tipo.grabacion_clase')}</span>}
        {video.bloqueado && <span className="video-card-lock">{t('videos.solo_miembros')}</span>}
      </div>
    </article>
  );
}

export function ReproductorModal({ video, onClose }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/videos/${video.id}/reproducir`, { headers: authHeaders() })
      .then(async (r) => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.code || 'HTTP ' + r.status); return j; })
      .then((j) => { if (!cancelled) setSrc(j); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { cancelled = true; document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [video.id, onClose]);

  return createPortal(
    <div className="galeria-modal" role="dialog" aria-modal="true" aria-label={video.titulo} onClick={onClose}>
      <div className="galeria-modal-inner video-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="galeria-cerrar" onClick={onClose} aria-label={t('galeria.cerrar')}><X size={20} /></button>
        <h3 className="galeria-modal-titulo">{video.titulo}</h3>
        <div className="video-marco">
          {error ? (
            <p className="galeria-vacia">{error === 'MEMBRESIA_REQUERIDA' ? t('videos.solo_miembros') : t('videos.error')}</p>
          ) : !src ? (
            <Loader2 size={28} className="spin" />
          ) : src.tipo === 'video' ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video src={src.url} controls autoPlay playsInline controlsList="nodownload" />
          ) : src.tipo === 'embed' ? (
            <iframe src={src.url} title={video.titulo} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
          ) : (
            <a className="btn-primary" href={src.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={15} /> {t('videos.abrir_enlace')}
            </a>
          )}
        </div>
        {video.descripcion && <p className="video-modal-desc">{video.descripcion}</p>}
      </div>
    </div>,
    document.body
  );
}
